import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ChartService } from 'app/shared/services/chart.service';
import { merge } from 'lodash';
import { hexToRgb } from 'app/shared/utils/colors';
import { LocalizedDatePipe } from '~common/app/shared/pipes/localized-date.pipe';

export interface ChartPerformanceData {
  labels: string[];
  data: Array<{
    [key: string]: number;
  }>;
}

// Both y axes are pinned to this width so charts stacked on a page line up, whether or not
// they have a second series and however long their tick labels are.
const Y_AXIS_WIDTH = 55;

function toKilo(value: number, max = 1000): string {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= max) {
    return (value / 1000).toFixed(1) + 'k';
  }
  return value.toString();
}

@Component({
  selector: 'app-dashboard-chart-performance',
  templateUrl: './dashboard-chart-performance.component.html',
  styleUrls: ['./dashboard-chart-performance.component.sass'],
  providers: [LocalizedDatePipe]
})
export class ChartPerformanceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() heading: string;
  @Input() data: ChartPerformanceData = null;
  @Input() granularity = 'month';
  @Input() isCurrency = false;

  chartOptions: any = null;
  chartData: any = null;

  private langChange: Subscription;
  private rebuildingForLanguage = false;

  constructor(
    private chartService: ChartService,
    private translateService: TranslateService,
    private datePipe: LocalizedDatePipe,
  ) { }

  ngOnInit() {
    // The heading follows the language through the translate pipe; the legend labels are baked
    // into chartData, so they need an explicit rebuild.
    this.langChange = this.translateService.onLangChange.subscribe(() => {
      if (this.chartData) {
        this.rebuildingForLanguage = true;
        this.normalizeData(this.data);
        this.rebuildingForLanguage = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.langChange) {
      this.langChange.unsubscribe();
    }
  }

  ngOnChanges() {
    if (this.data && this.data.labels && this.data.data && this.data.data.length > 0) {
      this.normalizeData(this.data);
    }
  }

  normalizeData(data: ChartPerformanceData) {
    if (!data || !data.labels || !data.data || data.data.length === 0) {
      this.chartData = null;
      return;
    }

    const dataKeys = Object.keys(data.data[0] || {});
    const hasTwoDatasets = dataKeys.length === 2;

    const theme = {
      success: '#0b65ff',
      primary: '#8b5cf6',
      secondary: '#3b4b62',
    };

    const firstKey = dataKeys[0];
    const firstData = data.data.map(item => item[firstKey]);

    const secondKey = dataKeys[1];
    const secondData = hasTwoDatasets ? data.data.map(item => item[secondKey]) : [];

    const formatDate = (value: string, format: string) => {
      const date = this.datePipe.transform(value, format);
      return date[0].toUpperCase() + date.slice(1);
    };

    // The API clamps the first and last label to the requested bounds when the period only
    // covers part of that bucket, so a monthly label landing anywhere but the 1st is a partial
    // month and must not be shown as a plain "March 2026".
    const isPartialBucket = (value: string) =>
      this.granularity === 'month' && new Date(value).getUTCDate() !== 1;

    let stepSize = 1;
    if (data.labels.length > 12) {
      stepSize = 2;
    }
    if (data.labels.length > 31) {
      stepSize = 3;
    }

    this.chartData = {
      labels: data.labels,
      datasets: [
        {
          type: 'bar',
          label: this.translateLabel(firstKey),
          fill: false,
          backgroundColor: theme.success,
          borderColor: theme.success,
          lineTension: 0,
          borderWidth: 2,
          pointBackgroundColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 3,
          maxBarThickness: 50,
          data: firstData,
          yAxisID: 'first',
          // Chart.js sorts datasets by `order` ascending then draws that list backwards, so the
          // HIGHEST order is painted first and ends up underneath. The bar takes the higher
          // value to sit behind the line.
          order: 1
        }
      ]
    };

    if (hasTwoDatasets) {
      this.chartData.datasets.push({
        type: 'line',
        label: this.translateLabel(secondKey),
        fill: true,
        backgroundColor: hexToRgb(theme.primary, 0.1),
        borderColor: theme.primary,
        lineTension: 0.4,
        borderWidth: 2,
        pointBackgroundColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 3,
        data: secondData,
        yAxisID: 'second',
        order: 0
      });
    }

    const baseOptions = this.chartService.getChartOptions() || {};

    this.chartOptions = merge(baseOptions, {
      // Only the legend labels change on a language switch, so replaying the entry animation
      // would redraw identical data for no reason. 1000 is the Chart.js default.
      animation: {
        duration: this.rebuildingForLanguage ? 0 : 1000
      },

      legend: {
        display: true
      },

      layout: {
        padding: {
          top: 0,
          bottom: 0
        }
      },

      hover: {
        mode: 'index',
        intersect: false
      },

      tooltips: {
        titleSpacing: 5,
        bodySpacing: 5,
        bodyAlign: 'left',
        backgroundColor: theme.secondary,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (items: any) => {
            // read the raw ISO label rather than xLabel, which the time scale has already
            // formatted to the axis unit and would hide the day
            const raw = this.chartData.labels[items[0].index];
            if (isPartialBucket(raw)) {
              return formatDate(raw, 'dd MMMM yyyy');
            }
            const format = this.granularity === 'month' ? 'MMMM yyyy' : 'dd MMMM yyyy';
            return formatDate(raw, format);
          },
          label: (tooltipItem: any, data: any) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            const value = tooltipItem.yLabel;
            let formatted = Math.round(value).toLocaleString();
            if (this.isCurrency) {
              formatted = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
            }
            return `${dataset.label}: ${formatted}`;
          }
        }
      },

      scales: {
        xAxes: [{
          type: 'time',
          time: {
            stepSize: stepSize,
            unit: this.granularity === 'month' ? 'month' : 'day',
            displayFormats: 'YYYY-MM-DD'
          },
          offset: true,
          gridLines: {
            display: false
          },
          ticks: {
            beginAtZero: true,
            padding: 10,
            callback: (value: string) => {
              const format = this.granularity === 'month' ? 'MMM yyyy' : 'dd/MM';
              return formatDate(value, format);
            },
          },
        }],

        yAxes: [
          {
            // the bar series: always the left axis, always blue, so the first axis reads the
            // same way on every chart
            id: 'first',
            type: 'linear',
            display: true,
            position: 'left',
            afterFit: (scale: any) => { scale.width = Y_AXIS_WIDTH; },
            gridLines: {
              display: true,
              drawBorder: false,
            },
            ticks: {
              beginAtZero: true,
              fontColor: theme.success,
              callback: (value: number) => toKilo(value)
            }
          },
          {
            // the line series: the right axis, purple. Kept mounted even with a single series so
            // the plot area keeps the same width; only its ticks are hidden.
            id: 'second',
            type: 'linear',
            display: true,
            position: 'right',
            afterFit: (scale: any) => { scale.width = Y_AXIS_WIDTH; },
            gridLines: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              display: hasTwoDatasets,
              beginAtZero: true,
              fontColor: theme.primary,
              callback: (value: number) => toKilo(value)
            }
          }
        ]
      }
    });
  }

  private formatLabel(key: string): string {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private translateLabel(key: string): string {
    const translationKey = `dashboard.charts.labels.${key.replace(/_/g, '-')}`;
    const translated = this.translateService.instant(translationKey);
    return translated !== translationKey ? translated : this.formatLabel(key);
  }
}
